<template>
  <div v-if="listing.preferred_photo != ''" >
    <div
      class="relative bg-gray-200 flex flex-col outline-1 outline-transparent outline-offset-0 group hover:outline-accent focus-within:outline-accent w-full rounded hover:ring hover:ring-inset hover:ring-accent focus-within:ring focus-within:ring-inset focus-within:ring-accent"
    >

      <a
        class="absolute z-20 top-0 left-0 right-0 bottom-0 block" tabindex="0"
        :href="'/listing/' + listing.rets_listing_id + '/'"
        target="_blank"
      >
        <div class="sr-only">
          <p
            class="antialiased text-primary-dark capitalize tracking-wide leading-5 opacity-90"
            v-html="address"
          />
          <p
            class="text-primary-dark capitalize antialiased "
            v-html="listing.city + ', ' + listing.state"
          />
          <p
            v-if="price > 0 && listing.monthly_rent == 0"
            class="text-3xl text-primary font-semibold py-2 flex items-center"
          >
            ${{ price.toLocaleString() }}
          </p>
          <p
            v-if="listing.monthly_rent > 0"
            class="text-3xl text-primary font-semibold py-2"
          >
            ${{ listing.monthly_rent.toLocaleString() }}/mo.
          </p>
        </div>
      </a>

    <div class="relative">

      <div
        v-if="listing.preferred_photo"
        class="w-full aspect-image rounded-t relative z-0 bg-center bg-cover border-b border-primary-dark lazy overflow-hidden"
        v-lazy:background-image="listing.preferred_photo"
      ></div>

      <div class="flex items-center m-2 w-full ">
        <favorite-button
          :mls="listing.rets_listing_id"
          :logged-in="loggedIn"
          :user="user"
          on-color="error"
          class="absolute z-30 right-12 mr-2 top-2 h-10 w-10 flex justify-center items-center cursor-pointer rounded focus:outline-primary focus:ring focus:ring-accent"
          @changed="$emit('set-favorite', { mls: listing.rets_listing_id})"
        >
          <svg-vue
            icon="heart"
            class="fill-current h-10 w-10 p-2.5 rounded bg-primary-dark bg-opacity-90 lg:hover:bg-opacity-100 lg:hover:text-white lg:focus:bg-opacity-100 lg:focus:text-white"
          />
        </favorite-button>

        <mini-photo-slider
          :photos="listing.photos.data"
          :listing_id="listing.rets_listing_id"
          class="absolute z-30 right-0 mr-2 top-2 h-10 w-10 flex justify-center items-center cursor-pointer shadow-lg focus:outline-primary focus:ring focus:ring-accent"
        ></mini-photo-slider>

      </div>
    </div>

      <strong v-if="listing.status == 'Sold/Closed'" class="listing-status px-4 leading-normal" tabindex="0"
        >Sold on {{ soldDate }}<br>for ${{ soldPrice }}</strong>

      <strong v-else-if="listing.status == 'Pending'" class="listing-status px-4" tabindex="0"
        >Sale Pending</strong>

      <strong v-else-if="listing.status == 'Contingent'" class="listing-status px-4" tabindex="0"
        >Under Contract</strong>

      <strong
        v-else-if="listing.original_list_price > listing.price && listing.status == 'Active'"
        class="listing-status px-4" tabindex="0"
        >Reduced
        <span class="text-error line-through" >${{ Math.trunc(parseFloat(listing.original_list_price)).toLocaleString() }}</span>
      </strong>

      <strong
        v-else-if="listing.has_open_houses == 1 && listing.status == 'Active'"
        class="listing-status px-4" tabindex="0"
        >Open House</strong>

      <span v-else-if="isNew" class="listing-status px-4" tabindex="0">Just Listed</span>

      <div class="text-center text-xl lg:text-lg flex-grow flex flex-col -mt-8" >
        <div class="bg-white py-2 mx-8 -mt-2 relative z-20 border-primary-dark border">
          <p
            class="antialiased text-primary-dark tracking-wide leading-5 text-lg md:text-base"
            v-html="address"
          />
          <p
            class="text-primary-dark font-semibold capitalize antialiased text-lg md:text-base"
            v-html="listing.city + ', ' + listing.state"
          />
        </div>

        <p
          v-if="price"
          class="text-3xl text-accent font-semibold pt-2 mt-2 mx-8"
        >
          ${{ price.toLocaleString() }}
        </p>
        <p
          v-if="listing.monthly_rent"
          class="text-3xl text-accent font-semibold pt-2 mt-2 mx-8"
        >
          ${{ listing.monthly_rent.toLocaleString() }}/mo.
        </p>

        <div class="flex justify-evenly pb-6 px-6">
          <div class="px-3 py-1" v-if="beds > 0">
            <div class="listing-icons">
              <svg-vue icon="beds" class="fill-current mt-1 h-6 w-auto mx-auto" ></svg-vue>
            </div>
            <div class="listing-icon-number">
              {{ beds }}
            </div>
            <div class="listing-icon-label">BEDS</div>
          </div>

          <div class="px-3 py-1" v-if="baths > 0">
            <div class="listing-icons">
              <svg-vue icon="baths" class="fill-current h-8 w-auto mx-auto" ></svg-vue>
            </div>
            <div class="listing-icon-number">
              {{ fullBaths + "/" + halfBaths }}
            </div>
            <div class="listing-icon-label">BATHS</div>
          </div>

          <div class="px-3 py-1" v-if="sqft > 0">
            <div class="listing-icons">
              <svg-vue icon="sqft" class="fill-current h-7 w-auto mx-auto" ></svg-vue>
            </div>
            <div class="listing-icon-number">
              {{ sqft }}
            </div>
            <div class="listing-icon-label">SQFT</div>
          </div>

          <div class="px-3 py-1" v-if="acres > 0 && !beds">
            <div class="listing-icons">
              <svg-vue icon="acreage" class="fill-current h-7 w-auto mx-auto" ></svg-vue>
            </div>
            <div class="listing-icon-number">
              {{ acres }}
            </div>
            <div class="listing-icon-label">ACRES</div>
          </div>
        </div>

        <p
          class="uppercase text-base antialiased tracking-wide text-center text-primary-dark bg-accent py-2 font-semibold rounded-b overflow-hidden"
        >
          MLS# {{ listing.rets_listing_id }}
        </p>
      </div>

    </div>
  </div>
</template>
<script>
import FavoriteButton from './FavoriteButton.vue'
import MiniPhotoSlider from './MiniPhotoSlider.vue'

export default {
  name: "ListingCard",

  components: {
    FavoriteButton,
    MiniPhotoSlider
  },

  props: {
    listing: {
      type: Object,
      default: () => {},
    },
    loggedIn: {
      type: Boolean,
      default: false
    },
    user: {
      type: Number,
      default: null
    },
  },

  computed: {
    price() {
      return this.listing.list_price
        ? Math.trunc(parseFloat(this.listing.list_price))
        : 0;
    },
    address() {
      return (
        this.listing.street_num +
        " " +
        this.listing.street_name +
        (this.listing.unit_num != undefined && this.listing.unit_num != ""
          ? ', <span class="inline-block">' + this.listing.unit_num + "</span>"
          : "")
      );
    },
    isNew() {
      return this.threedaysago < this.listed;
    },
    threedaysago() {
      let threedaysago = new Date();
      return threedaysago.setDate(threedaysago.getDate() - 3);
    },
    listed() {
      return Date.parse(this.listing.date_listed);
    },
    soldDate() {
      if (this.listing.sold_date) {
        let sold = this.listing.sold_date.split("-");
        return sold[1] + "/" + sold[2] + "/" + sold[0];
      }
    },
    soldPrice() {
      if (this.listing.sold_price) {
        return (this.listing.sold_price
        ? Math.trunc(parseFloat(this.listing.sold_price))
        : 0).toLocaleString();
      }
    },
    beds() {
      return this.listing.bedrooms
        ? Math.trunc(parseFloat(this.listing.bedrooms))
        : false;
    },
    baths() {
      return this.listing.baths
        ? Math.trunc(parseFloat(this.listing.baths))
        : false;
    },
    halfBaths() {
      return this.listing.baths_half
        ? Math.trunc(parseFloat(this.listing.baths_half))
        : 0;
    },
    fullBaths() {
      return this.listing.baths_full
        ? Math.trunc(parseFloat(this.listing.baths_full))
        : 0;
    },
    sqft() {
      return this.listing.sqft_hc
        ? Math.trunc(parseFloat(this.listing.sqft_hc))
        : this.listing.sqft_total ? this.listing.sqft_total : false;
    },
    acres() {
      return this.listing.acreage ? parseFloat(this.listing.acreage) : false;
    },
  },
}
</script>
